/**
 * ASR provider backed by the OS speech recognizer through Capacitor
 * (`@capgo/capacitor-speech-recognition`). Exists because the Android WebView
 * has no Web Speech API, so inside the app recognition must be native.
 *
 * Same contract as {@link WebSpeechRecognitionProvider}: partial results are
 * emitted as interim, and exactly one `isFinal` result is emitted when the
 * session ends with text. The native plugin does not flag the final phrase, so
 * the last partial is promoted to final when it reports the session stopped.
 *
 * Pure (no React/store). This layer never calls `fetch`; the OS recognizer may
 * use the network on its own. The plugin is imported lazily so the web bundle
 * never pays for it.
 */
import { Capacitor, type PluginListenerHandle } from '@capacitor/core'
import {
  type SpeechRecognitionCallbacks,
  type SpeechRecognitionOptions,
  type SpeechRecognitionProvider,
  VoiceError,
  type VoiceErrorCode,
} from './types'

type NativePlugin = typeof import('@capgo/capacitor-speech-recognition').SpeechRecognition

const PLUGIN_NAME = 'SpeechRecognition'
/** How long stop() waits for the plugin to report `stopped` before forcing it. */
const STOP_GRACE_MS = 3000

/** Map a native recognizer code/message onto the stable {@link VoiceErrorCode}. */
export function mapNativeError(vendor: string): VoiceErrorCode {
  const v = vendor.toUpperCase()
  if (v.includes('PERMISSION')) return 'not-allowed'
  switch (v) {
    case 'NO_MATCH':
    case 'SPEECH_TIMEOUT':
      return 'no-speech'
    case 'AUDIO':
      return 'audio-capture'
    case 'NETWORK':
    case 'NETWORK_TIMEOUT':
    case 'SERVER':
    case 'SERVER_DISCONNECTED':
      return 'network'
    case 'UNSUPPORTED_LOCALE':
    case 'MODEL_DOWNLOAD_SCHEDULED':
    case 'ON_DEVICE_RECOGNITION_UNAVAILABLE':
      return 'language-not-supported'
    case 'CLIENT':
      return 'aborted'
    default:
      return 'unknown'
  }
}

/**
 * Full phrase so far. With continuous PTT the plugin splits the phrase across
 * restart cycles: `accumulated` holds the earlier cycles, `matches[0]` the
 * current one, and `accumulatedText` the whole thing on the final event.
 */
function transcriptOf(event: {
  matches?: string[]
  accumulated?: string
  accumulatedText?: string
  isRestarting?: boolean
}): string {
  if (event.accumulatedText) return event.accumulatedText.trim()
  const current = event.matches?.[0] ?? ''
  if (event.isRestarting) return (event.accumulated ?? current).trim()
  return [event.accumulated, current].filter(Boolean).join(' ').trim()
}

function vendorOf(err: unknown): string {
  if (err && typeof err === 'object') {
    const { code, message } = err as { code?: unknown; message?: unknown }
    if (typeof code === 'string' && code) return code
    if (typeof message === 'string' && message) return message
  }
  return typeof err === 'string' ? err : 'unknown'
}

interface Session {
  callbacks: SpeechRecognitionCallbacks
  lastText: string
  started: boolean
  errored: boolean
  finished: boolean
  stopRequested: boolean
  aborted: boolean
  continuous: boolean
  /** Id of the native session, learned from its first `started` event. */
  nativeSessionId: number | null
  plugin: NativePlugin | null
  handles: Array<Promise<PluginListenerHandle>>
  forceTimer: ReturnType<typeof setTimeout> | null
}

export class NativeCapacitorRecognitionProvider implements SpeechRecognitionProvider {
  readonly id = 'native-capacitor'

  private session: Session | null = null
  /** Native id of the last session that ended: its late events are never ours. */
  private lastEndedNativeId: number | null = null

  isSupported(): boolean {
    return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable(PLUGIN_NAME)
  }

  start(callbacks: SpeechRecognitionCallbacks, options: SpeechRecognitionOptions = {}): void {
    if (this.session) return
    const s: Session = {
      callbacks,
      lastText: '',
      started: false,
      errored: false,
      finished: false,
      stopRequested: false,
      aborted: false,
      continuous: options.continuous ?? false,
      nativeSessionId: null,
      plugin: null,
      handles: [],
      forceTimer: null,
    }
    this.session = s
    void this.run(s, options)
  }

  stop(): void {
    const s = this.session
    if (!s || s.finished || s.stopRequested) return
    s.stopRequested = true
    if (!s.plugin) return // run() sees stopRequested right after setup
    // Releasing the button ends the held-PTT session: no more restarts.
    if (s.continuous) s.plugin.setPTTState({ held: false }).catch(() => undefined)
    s.plugin.stop().catch(() => undefined)
    s.forceTimer = setTimeout(() => {
      s.plugin?.forceStop().catch(() => undefined)
      this.finish(s, true)
    }, STOP_GRACE_MS)
  }

  abort(): void {
    const s = this.session
    if (!s || s.finished) return
    s.aborted = true
    s.plugin?.forceStop().catch(() => undefined)
    this.finish(s, false)
  }

  private fail(s: Session, code: VoiceErrorCode, vendor?: string): void {
    if (s.errored || s.finished) return
    s.errored = true
    s.callbacks.onError(new VoiceError(code, vendor))
  }

  private finish(s: Session, deliverFinal: boolean): void {
    if (s.finished) return
    s.finished = true
    if (s.forceTimer) clearTimeout(s.forceTimer)
    if (this.session === s) this.session = null
    if (s.nativeSessionId !== null) this.lastEndedNativeId = s.nativeSessionId
    if (deliverFinal && !s.errored && s.lastText) {
      s.callbacks.onResult({ transcript: s.lastText, isFinal: true })
    }
    for (const handle of s.handles) void handle.then((h) => h.remove()).catch(() => undefined)
    s.callbacks.onEnd?.()
  }

  private markStarted(s: Session): void {
    if (s.started || s.finished) return
    s.started = true
    s.callbacks.onStart?.()
  }

  private async run(s: Session, options: SpeechRecognitionOptions): Promise<void> {
    try {
      const { SpeechRecognition } = await import('@capgo/capacitor-speech-recognition')
      s.plugin = SpeechRecognition
      if (s.finished) return

      const { available } = await SpeechRecognition.available()
      if (s.finished) return
      if (!available) return this.failAndEnd(s, 'not-supported')

      const permission = await SpeechRecognition.requestPermissions()
      if (s.finished) return // aborted while the permission prompt was open
      if (permission.speechRecognition !== 'granted') {
        return this.failAndEnd(s, 'not-allowed', permission.speechRecognition)
      }

      s.handles.push(
        SpeechRecognition.addListener('partialResults', (event) => {
          if (s.finished) return
          const text = transcriptOf(event)
          if (!text) return
          s.lastText = text
          s.callbacks.onResult({ transcript: text, isFinal: false })
        }),
        SpeechRecognition.addListener('listeningState', (event) => {
          if (s.finished || !this.isOwnEvent(s, event.sessionId)) return
          const stopped = event.state === 'stopped' || (!event.state && event.status === 'stopped')
          if (event.state === 'started' || (!event.state && event.status === 'started')) {
            if (s.nativeSessionId === null && typeof event.sessionId === 'number') {
              s.nativeSessionId = event.sessionId
            }
            this.markStarted(s)
          }
          if (!stopped) return
          if (!s.errored && event.reason === 'error' && event.errorCode) {
            this.fail(s, mapNativeError(event.errorCode), event.errorCode)
          }
          this.finish(s, true)
        }),
        SpeechRecognition.addListener('error', (event) => {
          if (!this.isOwnEvent(s, event.sessionId)) return
          this.fail(s, mapNativeError(event.code), event.code)
        }),
      )
      await Promise.all(s.handles)

      if (s.finished) return // aborted while setting up
      if (s.stopRequested) return this.finish(s, false)

      // Held push-to-talk: the plugin only restarts across silence while it
      // believes the button is held, so tell it before it starts listening.
      if (s.continuous) {
        await SpeechRecognition.setPTTState({ held: true })
        if (s.finished) return
        if (s.stopRequested) return this.finish(s, false) // released before it started
      }
      await SpeechRecognition.start({
        language: options.lang,
        maxResults: 1,
        partialResults: options.interimResults ?? true,
        continuousPTT: s.continuous,
        popup: false,
      })
      if (s.finished) {
        // abort() landed while start() was in flight: the native session may
        // have opened anyway, so close it.
        SpeechRecognition.forceStop().catch(() => undefined)
        return
      }
      this.markStarted(s)
    } catch (err) {
      if (s.finished) return
      const vendor = vendorOf(err)
      this.failAndEnd(s, mapNativeError(vendor), vendor)
    }
  }

  /** Events of a previous native session can arrive late; ignore them. */
  private isOwnEvent(s: Session, eventSessionId: number | undefined): boolean {
    if (eventSessionId === undefined) return true
    if (s.nativeSessionId === null) return eventSessionId !== this.lastEndedNativeId
    return eventSessionId === s.nativeSessionId
  }

  private failAndEnd(s: Session, code: VoiceErrorCode, vendor?: string): void {
    this.fail(s, code, vendor)
    this.finish(s, false)
  }
}
